import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError, api } from '@/api/client';
import { FocusShell } from '@/components/layout';
import { Alert, Button, Card } from '@/components/ui';
import { useT } from '@/i18n';

type State = 'loading' | 'expired' | 'invalid' | 'error';

/**
 * `/d/:token` — where the WhatsApp "Download PDF" / "Download invoice" buttons and the report and
 * receipt emails land (spec WA9). No sign-in: the 7-day token is swapped for a short-lived file
 * link. An expired or unknown link offers sign-in, where the file is always available.
 */
/** Module-level so it is one stable function: a new one each render would re-run the effect. */
const leaveForFile = (url: string) => window.location.assign(url);

export const Download = ({ goTo = leaveForFile }: { goTo?: (url: string) => void }) => {
  const t = useT();
  const { token = '' } = useParams();
  const [state, setState] = useState<State>('loading');

  const open = useCallback(async () => {
    setState('loading');
    try {
      const { url } = await api.downloads.open(token);
      goTo(url);
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) setState('expired');
      else if (err instanceof ApiError && err.status === 404) setState('invalid');
      else setState('error');
    }
  }, [token, goTo]);

  useEffect(() => {
    void open();
  }, [open]);

  return (
    <FocusShell>
      <Card className="space-y-4">
        {state === 'loading' && <p className="text-body text-ink">{t('download.loading')}</p>}
        {(state === 'expired' || state === 'invalid') && (
          <>
            <Alert tone="info">{state === 'expired' ? t('download.expired') : t('download.invalid')}</Alert>
            <Button as="a" href="/reports">
              {t('download.signin')}
            </Button>
          </>
        )}
        {state === 'error' && (
          <>
            <Alert tone="warn">{t('common.error')}</Alert>
            <Button onClick={() => void open()}>{t('download.retry')}</Button>
          </>
        )}
      </Card>
    </FocusShell>
  );
};
